import {NavigationExtras, Router} from '@angular/router';

import {take} from 'rxjs/operators';

import {DialogService, DspxCardSetBaseFormComponent, GridDataHelpers, SpinnerService} from 'dspx-core';

import {API_ENDPOINT_KEYS} from '../../constants/api-endpoints.constants';
import {VERSIONS} from '../../constants/templates.constants';
import {APP_DATA_KEYS, NAV_TAB_NUMBERS, PAGE_NUMBERS} from '../../constants/app-data.constants';

import {DashboardResponse} from '../../interfaces/dashboard-response.interface';

import {ResponseParserService} from '../../services/response-parser.service';
import {AppDataService} from '../../services/app-data.service';
import {Subscription} from "rxjs";

export abstract class BaseCardSetListComponent {

  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;
  versionNo: number = VERSIONS.latest;
  model: any;
  configData: any = null;

  protected hasMoreItemsMessage: string;
  protected baseForm: DspxCardSetBaseFormComponent;
  protected subscriptions: Subscription[] = [];
  protected constructor(
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    protected templateId: number,
    protected getListApiEndpoint: () => string,
    protected hasFilters: boolean
  ) {
    const maxGridRowCount = this.appDataService.getAppData(APP_DATA_KEYS.maxGridRowCount);
    const extraMessage = this.hasFilters
      ? `Please use the filters to refine your search criteria.`
      : `Only the first ${maxGridRowCount} items are being displayed.`;
    this.hasMoreItemsMessage = `
      <div class="p-grid">
        <div><strong>The search results have exceeded the ${maxGridRowCount} item limit. ${extraMessage}</strong></div>
      </div>
    `;
  }

  // Unsubscribe from the all the subscriptions registered
  // with 'registerSubscription'. Add this method to the
  // ngOnDestroy method on the component
  unsubscribeSubscriptions(): void {
    this.subscriptions.forEach((item) => {
      if (item instanceof Subscription) {
        try {
          item.unsubscribe();
        }
        catch (e) {
          console.log('There was an issue trying to unsubscribe from an observable.', e);
        }
      }
    });
  }

  // Registering a subscription through this method will allow it to
  // be unsubscribed when the component is destroyed. Ensure that
  // unsubscribeSubscriptions() is called in the components ngOnDestroy
  registerSubscription(subscription): void {
    this.subscriptions.push(subscription);
  }

  protected loadGridDataOnFormLoad<T>(filterRequest?: T, configData?: any): void {
    this.configData = configData;

    // This guarantees that the spinner is displayed during the execution of loadGridData, when loadGridData is called from within the onFormLoad event handler.
    // The caller of the onFormLoad event handler calls hideSpinner after the event handler returns.
    // This causes the call to showSpinner inside loadGridData to be useless because it is immediately followed by a call to hideSpinner.
    // To work around the issue, we call loadGridData on the next event cycle, so that the call to showSpinner inside loadGridData happens after call to hideSpinner.
    setTimeout(() => {
      this.loadGridData(filterRequest);
    });
  }

  protected loadGridData<T>(filterRequest?: T): void {
    this.spinnerService.showSpinner();
    this.appDataService.post(this.getListApiEndpoint(), filterRequest ?? { initialGridLoad: true })
      .pipe(take(1))
      .subscribe((data: DashboardResponse) => {
        this.spinnerService.hideSpinner();
        if (this.responseParserService.findPageModel(data?.response, NAV_TAB_NUMBERS.dashboardCommon.grid, PAGE_NUMBERS.dashboardCommon.grid, null)) {
          this.model = data.response;

          if (this.configData) {
            GridDataHelpers.ParseDatesInGrids(this.configData, this.model);
          }

          const statusBarContent = data?.hasMoreItems ? this.hasMoreItemsMessage : '';
          this.baseForm.setStatusBar(null, null, statusBarContent);
        }
      });
  }

  protected reloadPage() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([this.router.url]);
  }

  // NOTE: this method only works for questions inside non-repeating sections inside non-repeating pages
  protected getQuestionValue(model: any, navTabNumber: number, pageNumber: number, sectionCode: string, questionCode: string): any {
    return this.responseParserService.findQuestionModel(model, navTabNumber, pageNumber, null, sectionCode, null, questionCode);
  }

  /* 
   * Routes the received event to an appropriately named handler function on this component class.
   *
   * The name of the handler function is an underscore-delimited string with the following segments:
   *   $event.field?.key,
   *   $event.field?.field?.key,
   *   $event.field?.type,
   *   $event.event?.type,
   *   $event.event?.colName,
   *   $event.event?.data?.buttonType,
   *   "Clicked"
   *
   * Any segments that are undefined are omitted entirely.
   *
   * Example of a colLink click on the column "complianceCheckId" on the grid "complianceCheckListPlaceHolder":
   *   complianceCheckListPlaceholder_colLink_complianceCheckId_Clicked
   *
   * If an event handler cannot be found with the expected name, a console error is logged that mentions
   * the expected handler name.
   * 
   * @param $event
   * @returns true if an event handler was found and called, false otherwise.
   */
  protected handleActionButtonClick($event: any): boolean {
    // Build the name of the handler function to call based on the event's details
    let segments = [
      $event.field?.key,
      $event.field?.field?.key,
      $event.field?.type,
      $event.event?.type,
      $event.event?.colName,
      $event.event?.data?.buttonType,
      "Clicked"
    ];

    // Remove false-y segments
    segments = segments.filter((x) => !!x);
    segments = segments.map(x => x.replace('-', ''));

    let handlerName = "this.actionButton_" + segments.join("_");

    let handler = eval(handlerName);

    if (handler) {
      handler = handler.bind(this);

      handler($event);

      return true;
    } else {
      console.warn(`Could not find action button click handler. Expected ${handlerName}. Source event: `, $event);

      return false;
    }
  }

  public handleDialogButtonClick($event: any, popupCode: string): boolean {
    // Build the name of the handler function to call based on the event's details
    let segments = [
      $event?.field?.key,
      $event?.event?.type,
      $event?.event?.colName,
      $event?.event?.data?.buttonType,
      $event?.field?.field?.key,
      $event?.field?.type,
      popupCode,
      "Clicked"
    ];

    // Remove false-y segments
    segments = segments.filter((x) => !!x);

    const handlerName = "this.dialogButton_" + segments.join("_").replace("-", "");

    let handler = eval(handlerName);

    if (handler) {
      handler = handler.bind(this);

      handler($event);

      return true;
    } else {
      console.warn(`Could not find dialog button click handler. Expected ${handlerName}. Source event: `, $event);

      return false;
    }
  }
}
